import { useTranslation } from 'react-i18next';

import Delete2Line from '@/assets/icons/delete_2_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { confirm } from '@/components/molecules/ConfirmDialog';
import {
    createDataTableColumnHelper,
    DataTable,
    type DataTableCellContext,
    type DataTableColumnDef,
} from '@/components/organisms/DataTable';
import { formatDateTimeWorded, toDate } from '@/utils/date';

import styles from './MoveBioscope.module.scss';

interface Bioscope {
    id: number;
    lastCage?: string;
    updatedAt?: string;
    lastLocation?: string;
    lastMeasurement?: string;
}

interface MoveBioscopeContainerProps {
    bioscopes: Bioscope[];
    canMoveBioscope: boolean;
    onMoveBioscope: (bioscopeId: number) => void;
    /** Runs inside the confirmation, which waits for it and shows a failure. */
    onDeleteBioscope: (bioscopeId: number) => Promise<unknown>;
    highlightedBioscopeId: number | null;
}
export function MoveBioscope({
    bioscopes,
    canMoveBioscope,
    onDeleteBioscope,
    onMoveBioscope,
    highlightedBioscopeId,
}: MoveBioscopeContainerProps) {
    const { t } = useTranslation();
    const columnHelper = createDataTableColumnHelper<Bioscope>();

    function parseDate(date: string | null | undefined) {
        return date ? toDate(date) : null;
    }
    /*
    function dateSort<T extends object>(f: (T) => Date) {
        return (aOrig: T, bOrig: T) => {
            const a = f(aOrig);
            const b = f(bOrig);
            if (a === null && b === null) {
                return 0;
            } else if (a === null) {
                return -1;
            } else if (b === null) {
                return 1;
            } else {
                return a.getTime() - b.getTime();
            }
        };
    }
    */
    const timestampCell = (cell: DataTableCellContext<Bioscope, Date>) =>
        cell.getValue() ? formatDateTimeWorded(cell.getValue(), 'minute') : null;

    const columns: DataTableColumnDef<Bioscope>[] = columnHelper.columns([
        columnHelper.accessor((row) => row.id, {
            header: t('common.labels.id'),
            id: 'Id',
        }),
        columnHelper.accessor((row) => row.lastCage ?? t('common.labels.land'), {
            header: t('common.labels.cage'),
            id: 'Cage',
        }),
        columnHelper.accessor((row) => row.lastLocation ?? t('common.labels.land'), {
            header: t('moveBioscope.moveBioscope.site'),
            id: 'Facility',
        }),
        columnHelper.accessor((row) => parseDate(row.updatedAt), {
            header: t('moveBioscope.moveBioscope.lastMoved'),
            id: 'LastMoved',
            cell: timestampCell,
        }),
        columnHelper.accessor((row) => parseDate(row.lastMeasurement), {
            header: t('common.labels.lastMeasurement'),
            id: 'LastMeasurement',
            cell: timestampCell,
        }),
        columnHelper.accessor((row) => row.id, {
            header: t('common.labels.actions'),
            id: 'Actions',

            cell: (cell) => (
                <div className={styles['not-break']}>
                    <Button
                        variant={'table'}
                        size={'medium'}
                        disabled={!canMoveBioscope}
                        className={styles.moveButton}
                        trackingId="bioscope_move"
                        onClick={() => onMoveBioscope(cell.getValue())}>
                        {t('common.actions.move')}
                    </Button>
                    <Button
                        variant={'danger'}
                        size={'medium'}
                        disabled={!canMoveBioscope}
                        trackingId="bioscope_delete_last_move"
                        onClick={() =>
                            confirm({
                                title: t('common.actions.deleteLastMove'),
                                message: t('bioscopeDetail.placement.confirmDeleteLastMove'),
                                confirmLabel: t('common.actions.delete'),
                                errorFallback: t('bioscopeDetail.placement.updateError'),
                                onConfirm: () => onDeleteBioscope(cell.getValue()),
                            })
                        }>
                        <SvgIcon icon={Delete2Line} size="custom" customSize={20} />
                        {t('common.actions.deleteLastMove')}
                    </Button>
                </div>
            ),
        }),
    ]);

    return (
        <Card as="section" padding="xl">
            <DataTable
                responsive
                data={bioscopes}
                columns={columns}
                initialSort={[{ id: 'Id', desc: false }]}
                getRowProps={(row) => ({
                    className:
                        row.original.id === highlightedBioscopeId ? styles.highlightedRow : '',
                })}
            />
        </Card>
    );
}
